<template>
  <app-page
    title="Rewards settings"
    subtitle="Use this balance to fund employee rewards and recognition."
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
    back-route="/recognition"
  >
    <template v-slot:header-content>
      <app-stat
        :stat="ledger?.balance_minor ?? 0"
        :formatter="getCurrencyFormatter(ledger?.balance_minor ?? 0)"
        title="Balance"
        color="default"
        small
        class="header-stat"
      />
    </template>

    <template v-slot:custom-content>
      <app-loading :loading="isLoading" is-background />

      <template v-if="!isLoading">
        <b-col>
          <!-- Top up section -->
          <sme-card large class="mb-4">
            <b-row>
              <b-col lg="7">
                <h4 class="top-up-title mb-2">Top up your reward balance</h4>
                <p class="text-muted mb-3">
                  To top up your rewards balance, create a PDF invoice you can send to your finance team. Your wallet is
                  credited once the invoice is paid.
                </p>

                <p class="label-text mb-2">How this works:</p>
                <ol class="instructions-list">
                  <li>Enter the amount and a purchase order number.</li>
                  <li>We'll generate a PDF invoice with our bank details.</li>
                  <li>You send the invoice to your finance team.</li>
                  <li>When the invoice is paid, your rewards balance is topped up.</li>
                </ol>
              </b-col>

              <b-col lg="5">
                <ValidationObserver ref="formObserver" v-slot="v">
                  <b-form @submit.prevent="v.handleSubmit(onCreateInvoice)">
                    <app-input
                      v-model="amountInput"
                      name="Amount to add"
                      type="number"
                      :prepend="currencySymbol"
                      inputmode="decimal"
                      rules="required|min_value:1"
                      :validate-immediate="false"
                    >
                      <template #label><span class="label-text">Amount to add</span></template>
                    </app-input>

                    <app-input v-model="purchaseOrder" name="Purchase order" type="text">
                      <template #label>
                        <div class="d-flex justify-content-between w-100">
                          <span class="label-text">Add a purchase order</span>
                          <small class="text-muted">Optional</small>
                        </div>
                      </template>
                    </app-input>

                    <b-button type="submit" variant="primary" block :disabled="isCreatingInvoice" class="mt-3">
                      <b-spinner v-if="isCreatingInvoice" small class="mr-2" />
                      Create invoice
                    </b-button>
                  </b-form>
                </ValidationObserver>
              </b-col>
            </b-row>
          </sme-card>

          <!-- Invoices table -->
          <sme-card large>
            <app-loading :loading="isLoadingInvoices" />

            <p v-if="!isLoadingInvoices && invoices.length === 0" class="text-muted mb-0">
              You don't have any top-up invoices yet. Create one to get started.
            </p>

            <b-table
              v-if="!isLoadingInvoices && invoices.length > 0"
              :fields="invoiceFields"
              :items="invoices"
              table-class="table-minimal mb-0"
            >
              <template #cell(status)="{ item }">
                <span
                  class="status-badge"
                  :class="item.status === 'PAID' ? 'status-badge--paid' : 'status-badge--pending'"
                >
                  {{ item.status === 'PAID' ? 'Paid' : 'Pending' }}
                </span>
              </template>

              <template #cell(actions)="{ item }">
                <div class="d-flex align-items-center justify-content-end actions-cell">
                  <a
                    v-if="item.status === 'PENDING'"
                    v-b-tooltip.hover
                    href="#"
                    title="Delete invoice"
                    class="action-link"
                    @click.prevent="onDeleteInvoice(item)"
                  >
                    <font-awesome-icon :icon="['far', 'trash']" />
                  </a>
                  <a :href="item.pdf_url" target="_blank" rel="noopener" class="action-link">
                    <font-awesome-icon :icon="['far', 'eye']" />
                    View
                  </a>
                  <a href="#" class="action-link" @click.prevent="copyLink(item)">
                    <font-awesome-icon :icon="['far', 'link']" />
                    Copy link
                  </a>
                  <a href="#" class="action-link" @click.prevent="downloadPdf(item)">
                    <font-awesome-icon :icon="['far', 'download']" />
                    Download PDF
                  </a>
                </div>
              </template>
            </b-table>
          </sme-card>
        </b-col>
      </template>
    </template>

    <!-- Footer -->
    <template v-slot:footer>
      <b-modal
        v-model="showDeleteModal"
        title="Delete invoice"
        ok-variant="danger"
        ok-title="Delete"
        @ok="confirmDeleteInvoice"
      >
        <p v-if="invoiceToDelete">Are you sure you want to delete invoice {{ invoiceToDelete.invoice_number }}?</p>
      </b-modal>
    </template>
  </app-page>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { ValidationObserver } from 'vee-validate';
import { format } from 'date-fns';

import AppPage from '@/components/AppPage.vue';
import AppStat from '@/components/AppStat.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppInput from '@/components/AppInput.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';

import {
  getRecognitionLedger,
  getRecognitionInvoices,
  createRecognitionInvoice,
  deleteRecognitionInvoice,
} from '@/queries/recognition';
import { getCurrencyFormatter, toCurrency } from '@/utils';
import { currencySymbolFromCode } from '@/utils/common';
import useToast from '@/composables/useToast';
import State from '@/state/State';

const { doToast, TOAST_VARIANT } = useToast();

// Form state
const formObserver = ref(null);
const amountInput = ref('');
const purchaseOrder = ref('');
const invoiceToDelete = ref(null);

// Queries
const ledgerQuery = useQuery(getRecognitionLedger());
const invoicesQuery = useQuery(getRecognitionInvoices());
const createInvoiceMutation = useMutation(createRecognitionInvoice());
const deleteInvoiceMutation = useMutation(deleteRecognitionInvoice());

// Computed
const ledger = computed(() => ledgerQuery.data.value);
const invoices = computed(() => invoicesQuery.data.value || []);
const isLoading = computed(() => ledgerQuery.isPending.value);
const isLoadingInvoices = computed(() => invoicesQuery.isPending.value);
const isCreatingInvoice = computed(() => createInvoiceMutation.isPending.value);

const currencySymbol = computed(() => currencySymbolFromCode(State.state?.company?.default_currency));

// Table fields
const invoiceFields = [
  { key: 'invoice_number', label: 'Invoice' },
  {
    key: 'created_at',
    label: 'Created',
    formatter: value => format(new Date(value), 'dd MMM yyyy'),
  },
  {
    key: 'amount_minor',
    label: 'Amount',
    formatter: value => toCurrency(value),
  },
  {
    key: 'purchase_order',
    label: 'PO number',
    formatter: value => value || '—',
  },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: '', thStyle: { width: '1px' } },
];

// Methods
const onCreateInvoice = async () => {
  const amount = parseFloat(amountInput.value);
  if (!amount || amount <= 0) {
    return;
  }

  const payload = {
    amount_minor: Math.round(amount * 100),
  };

  if (purchaseOrder.value?.trim()) {
    payload.purchase_order = purchaseOrder.value.trim();
  }

  try {
    await createInvoiceMutation.mutateAsync(payload);
    doToast('Success', 'Invoice created', TOAST_VARIANT.SUCCESS);
    amountInput.value = '';
    purchaseOrder.value = '';
    requestAnimationFrame(() => formObserver.value?.reset());
  } catch {
    doToast('Error', 'Failed to create invoice', TOAST_VARIANT.DANGER);
  }
};

const showDeleteModal = ref(false);

const onDeleteInvoice = invoice => {
  invoiceToDelete.value = invoice;
  showDeleteModal.value = true;
};

const confirmDeleteInvoice = async () => {
  if (!invoiceToDelete.value) {
    return;
  }

  try {
    await deleteInvoiceMutation.mutateAsync(invoiceToDelete.value.recognition_ledger_deposit_invoice_id);
    doToast('Success', 'Invoice deleted', TOAST_VARIANT.SUCCESS);
  } catch {
    doToast('Error', 'Failed to delete invoice. It may have already been paid.', TOAST_VARIANT.DANGER);
  } finally {
    invoiceToDelete.value = null;
  }
};

const downloadPdf = async invoice => {
  try {
    const response = await fetch(invoice.pdf_url);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoice.invoice_number}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    doToast('Error', 'Failed to download PDF', TOAST_VARIANT.DANGER);
  }
};

const copyLink = async invoice => {
  try {
    await navigator.clipboard.writeText(invoice.pdf_url);
    doToast('Success', 'Link copied to clipboard', TOAST_VARIANT.SUCCESS);
  } catch {
    doToast('Error', 'Failed to copy link', TOAST_VARIANT.DANGER);
  }
};
</script>

<style scoped lang="scss">
:deep(.page-sub-header-wrapper) {
  align-self: flex-start;
}

.header-stat {
  height: auto;

  :deep(.app-stat__value-container) {
    width: 16rem;
    justify-content: flex-start;
  }

  :deep(.app-stat__description) {
    color: var(--palette-color-default-lighten-30);
  }
}

.top-up-title,
.label-text {
  color: var(--palette-color-default);
  font-weight: 600;
}

.instructions-list {
  padding-left: 1.25rem;
  margin-bottom: 0;

  li {
    margin-bottom: 0.25rem;
    color: var(--palette-color-default-lighten-20);
  }
}

.actions-cell {
  gap: 1.25rem;
  white-space: nowrap;
}

.status-badge {
  border-radius: 99px;
  padding: 0.125rem 0.375rem;
  font-weight: 500;

  &--pending {
    color: var(--palette-color-warning-darken-10);
    background-color: var(--palette-color-warning-lighten-95);
    border: 1px solid var(--palette-color-warning-lighten-80);
  }

  &--paid {
    color: var(--palette-color-success-darken-10);
    background-color: var(--palette-color-success-lighten-95);
    border: 1px solid var(--palette-color-success-lighten-80);
  }
}

.action-link {
  color: var(--palette-color-brand-primary);
  text-decoration: none;
  cursor: pointer;
  border: none;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  &:hover {
    text-decoration: underline;
  }
}
</style>
